/** PLAY FROM ANYWHERE — the player's screen at home. Registered players only.
 *
 *  SIGN IN ONLY. There is deliberately no "Create account", no "Set up my
 *  table" and no "Join this table": a forwarded link gives a stranger nothing.
 *  New players register at the venue, on the table card.
 *
 *  The frame, the logo and the wallpaper are the venue's own — the same
 *  `BrandShell` the fan screen in the room uses — and the scratch is the same
 *  `GoalScratch`. Nothing about the table game, its PIN or its prize is
 *  touched by anything here.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, LogIn, Star } from 'lucide-react';
import { GoalScratch } from '../../components/goalgame/GoalScratch';
import { GoalBeacon } from '../../components/goalgame/GoalBeacon';
import { BrandShell, palette, typo } from '../../components/goalgame/GoalFanView';
import { StarBar, StarList } from '../../components/goalgame/GoalStarPick';
import { absUrl } from '../../components/patron/PatronPageView';
import { bodyPx, fontStack, headlinePx, loadFont } from '../../lib/designTokens';

const API = `${(typeof window !== 'undefined' ? window.location.origin : '')}/api/goal-remote/public/link`;
const BRAND = `${(typeof window !== 'undefined' ? window.location.origin : '')}/api/goal-game/public`;
const DEVICE_KEY = 'yot_remote_device';

const deviceId = () => {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
        id = `rm-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
        localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
};

/** The venue's Patron look — the SAME frame `PatronPageView` draws: its
 *  background and text colours, its wallpaper with its own darkness, its two
 *  logos, its fonts. Not a copy of the document, and not a second editor: the
 *  remote endpoint hands over what the Patron pages editor already saved. A
 *  venue that has never opened that editor sends `saved: false` and this is
 *  never used, so its screen stays exactly as it is today. */
const PatronLook = ({ look, children }) => {
    const d = look.design || {};
    const b = look.branding || {};
    const wallpaper = absUrl(b.wallpaper_url);
    // The venue's own wallpaper fit, zoom and position — the same handling as
    // the Patron page, so a design set once looks the same in both places.
    const zoom = Math.max(100, Math.min(200, Number(b.wallpaper_zoom) || 100));
    const posX = Number.isFinite(Number(b.wallpaper_x)) ? Number(b.wallpaper_x) : 50;
    const posY = Number.isFinite(Number(b.wallpaper_y)) ? Number(b.wallpaper_y) : 50;
    return (
        <div className="min-h-screen w-full relative overflow-hidden"
             style={{ backgroundColor: d.bg, color: d.text,
                      fontFamily: fontStack(d.body_font) || undefined,
                      fontSize: bodyPx(d.body_size) || undefined }}
             data-testid="remote-patron-look">
            {wallpaper && (
                <>
                    <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
                        <div className="absolute inset-0 bg-no-repeat"
                             style={{ backgroundImage: `url(${wallpaper})`,
                                      backgroundSize: b.wallpaper_fit === 'whole' ? 'contain' : 'cover',
                                      backgroundPosition: `${posX}% ${posY}%`,
                                      transform: zoom === 100 ? undefined : `scale(${zoom / 100})`,
                                      transformOrigin: `${posX}% ${posY}%` }}
                             data-testid="remote-look-wallpaper" />
                    </div>
                    {/* the venue's own darkness, so its text stays readable on
                        any picture it picks — the same handling as the Patron page */}
                    <div className="absolute inset-0"
                         style={{ backgroundColor: `rgba(0,0,0,${(Number(b.wallpaper_dark) || 0) / 100})` }}
                         aria-hidden="true" data-testid="remote-look-wallpaper-dark" />
                </>
            )}
            <div className="relative mx-auto w-full max-w-sm px-5 pt-8 pb-14">
                {(b.merchant_logo_url || b.program_logo_url) && (
                    <div className="flex flex-col items-center gap-2 mb-5">
                        {b.merchant_logo_url && (
                            <img src={absUrl(b.merchant_logo_url)} alt="" className="object-contain"
                                 style={{ maxWidth: '100%', height: Number(b.merchant_logo_px) || 96 }}
                                 data-testid="remote-look-merchant-logo" />
                        )}
                        {b.program_logo_url && (
                            <img src={absUrl(b.program_logo_url)} alt="" className="object-contain"
                                 style={{ maxWidth: '100%', height: Number(b.program_logo_px) || 72 }}
                                 data-testid="remote-look-program-logo" />
                        )}
                    </div>
                )}
                {children}
            </div>
        </div>
    );
};

const ERRORS = {
    not_registered: 'remoteNotRegistered',
    mismatch: 'remoteWrongPin',
    pin_four_digits: 'remotePin',
    locked: 'remoteLocked',
};

const dateText = (iso, lang) => (iso
    ? new Date(iso).toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA',
        { month: 'short', day: 'numeric', year: 'numeric' })
    : '');

const prizeTitle = (prize, lang) => (!prize ? ''
    : (lang === 'fr' ? prize.title_fr || prize.title_en : prize.title_en || prize.title_fr) || '');

/** The player's own prizes, remote and table alike, with the code to show. */
const MyPrizes = ({ prizes, c, t, lang }) => (!prizes?.length ? null : (
    <div className="mt-6 rounded-2xl border border-white/15 bg-black/25 p-4" data-testid="remote-prizes">
        <p className="text-[10px] font-semibold uppercase tracking-wider opacity-60 mb-2">
            {t('goalGame.remoteMyPrizes')}
        </p>
        <ul className="space-y-2">
            {prizes.map((p) => (
                <li key={p.code} data-testid={`remote-prize-${p.code}`}>
                    <span className="block text-sm font-bold">
                        {(lang === 'fr' ? p.title_fr : p.title) || p.title}
                    </span>
                    <span className="block font-mono text-base tracking-[0.18em]"
                          style={{ color: c.accent }} data-testid={`remote-prize-code-${p.code}`}>
                        {p.code}
                    </span>
                    <span className="block text-[11px] opacity-60">
                        {p.status === 'redeemed'
                            ? t('goalGame.remotePrizeRedeemed')
                            : t('goalGame.remotePrizeExpires', { when: dateText(p.expires_at, lang) })}
                        {p.remote_play ? ` · ${t('goalGame.remotePrizeRemote')}` : ''}
                    </span>
                </li>
            ))}
        </ul>
    </div>
));

export default function RemotePlay() {
    const { token } = useParams();
    const { t, i18n } = useTranslation();
    const lang = (i18n.language || 'en').slice(0, 2);
    const [state, setState] = useState(null);
    const [brand, setBrand] = useState(null);
    const [dead, setDead] = useState(false);
    const [email, setEmail] = useState('');
    const [pin, setPin] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [outcome, setOutcome] = useState(null);
    const [picking, setPicking] = useState(false);
    const [left, setLeft] = useState(0);
    const scratched = useRef(false);

    const load = useCallback(async () => {
        try {
            const res = await fetch(`${API}/${token}/state?device_id=${encodeURIComponent(deviceId())}&lang=${lang}`);
            if (!res.ok) throw new Error('link');
            const body = await res.json();
            setState(body);
            setLeft(body.event?.seconds_left || 0);
            return body;
        } catch (_e) { setDead(true); return null; }
    }, [token, lang]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        const id = setInterval(load, 5000);
        return () => clearInterval(id);
    }, [load]);

    useEffect(() => {
        const d = state?.patron_look?.saved ? state.patron_look.design : null;
        if (!d) return;
        loadFont(d.headline_font);
        loadFont(d.body_font);
    }, [state?.patron_look]);

    useEffect(() => {
        if (!state?.card_id || brand) return;
        fetch(`${BRAND}/${state.card_id}/brand`)
            .then((r) => (r.ok ? r.json() : null))
            .then((b) => b && setBrand(b))
            .catch(() => {});
    }, [state?.card_id, brand]);

    // The countdown runs on the phone between polls, exactly as the venue
    // screen does; the server's own `closes_at` is what actually ends it.
    useEffect(() => {
        if (!left) return undefined;
        const id = setInterval(() => setLeft((s) => Math.max(0, s - 1)), 1000);
        return () => clearInterval(id);
    }, [left]);

    const post = async (path, payload) => {
        const res = await fetch(`${API}/${token}/${path}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ device_id: deviceId(), lang, ...payload }),
        });
        const body = await res.json().catch(() => ({}));
        return { ok: res.ok, body };
    };

    const signIn = async (e) => {
        e.preventDefault();
        setBusy(true); setError('');
        const { ok, body } = await post('sign-in', { email, pin });
        setBusy(false);
        if (!ok) { setError(ERRORS[body?.detail] || 'remoteWrongPin'); return; }
        setState(body);
    };

    const onScratched = async () => {
        if (scratched.current) return;
        scratched.current = true;
        const { ok, body } = await post('play', { event_id: state.event.id });
        if (ok) { setOutcome(body); setState(body.state); } else { await load(); }
    };

    const pickStar = async (playerId) => {
        setBusy(true);
        const { ok, body } = await post('star-pick', { player_id: playerId });
        setBusy(false);
        if (ok) { setState(body); setPicking(false); }
    };

    const toggleKeep = async () => {
        const { ok, body } = await post('keep-star', { keep: !state.me?.keep_star });
        if (ok) setState(body);
    };

    if (dead) {
        return (
            <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-6 text-center"
                 data-testid="remote-link-dead">
                <p className="text-sm opacity-80">{t('goalGame.remoteLinkDead')}</p>
            </div>
        );
    }
    if (!state) {
        return (
            <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center"
                 data-testid="remote-loading">
                <Loader2 className="animate-spin opacity-50" size={22} />
            </div>
        );
    }

    // The Patron design wins where the venue has set one; everything else keeps
    // the Goal Game fan-screen look it has today.
    const look = state.patron_look?.saved ? state.patron_look : null;
    const d = look?.design || {};
    const c = look ? { ...palette(brand), accent: d.accent, button: d.button } : palette(brand);
    const ty = look
        ? { headline: { fontFamily: fontStack(d.headline_font) || undefined,
                        fontSize: headlinePx(d.headline_size) || undefined,
                        // a tall display face at the venue's own size needs its
                        // own leading, or the line below it gets walked on
                        lineHeight: 1.1 } }
        : typo(brand);
    const field = 'w-full rounded-xl bg-white/10 border border-white/15 px-4 py-3 text-base outline-none';
    const panel = 'rounded-2xl border border-white/12 bg-white/5 p-5';
    const primary = { backgroundColor: c.button, color: '#ffffff' };
    const me = state.me || {};
    const open = state.event?.status === 'open' && left > 0;
    const won = outcome?.result === 'win' || state.result === 'win';
    const header = (
        <div className="mb-6">
            <p className="text-xs uppercase tracking-[0.2em] opacity-50" data-testid="remote-venue">
                {state.venue}
            </p>
            <h1 className="text-3xl font-black leading-tight" style={ty.headline}
                data-testid="remote-title">{t('goalGame.remoteTitle')}</h1>
            {state.game_label && (
                <p className="text-base font-semibold mt-1" data-testid="remote-game">
                    {t('goalGame.remoteGameLine', { game: state.game_label })}
                </p>
            )}
        </div>
    );

    const shell = (children) => (look ? (
        <PatronLook look={look}>
            <div data-testid="remote-screen">{header}{children}</div>
        </PatronLook>
    ) : (
        <BrandShell lang={lang} onLang={(code) => i18n.changeLanguage(code)} brand={brand}>
            <div data-testid="remote-screen">{header}{children}</div>
        </BrandShell>
    ));

    // ── not signed in: the ONLY thing offered is a sign-in ──────────────────
    if (!state.signed_in) {
        return shell(
            <form onSubmit={signIn} className={`${panel} space-y-3`} data-testid="remote-sign-in-form">
                <p className="text-sm opacity-75" data-testid="remote-sign-in-only">
                    {t('goalGame.remoteSignInOnly')}
                </p>
                <input type="email" value={email} autoComplete="email" className={field}
                       onChange={(e) => setEmail(e.target.value)}
                       placeholder={t('goalGame.remoteEmail')} data-testid="remote-email" />
                <input type="tel" inputMode="numeric" maxLength={4} value={pin}
                       onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                       placeholder={t('goalGame.remotePin')} data-testid="remote-pin"
                       className={`${field} tracking-[0.4em] text-center`} />
                <button type="submit" disabled={busy} style={primary}
                        data-testid="remote-sign-in-button"
                        className="w-full min-h-[44px] rounded-xl font-bold py-3 disabled:opacity-60 inline-flex items-center justify-center gap-2">
                    {busy ? <Loader2 className="animate-spin" size={18} />
                          : <><LogIn size={16} />{t('goalGame.remoteSignIn')}</>}
                </button>
                {error && <p className="text-sm text-red-300" data-testid="remote-error">{t(`goalGame.${error}`)}</p>}
            </form>
        );
    }

    // ── the star step, open any time except while a round is running ────────
    if (picking) {
        return shell(
            <div className={panel} data-testid="remote-star-step">
                <p className="text-lg font-bold mb-1" style={ty.headline}>{t('goalGame.starPickTitle')}</p>
                <p className="text-sm opacity-60 mb-4">{t('goalGame.starPickBody')}</p>
                <StarList players={me.players || []} selected={me.pick?.player_id}
                          onSelect={pickStar} c={c} field={field} t={t}
                          searchLabel={t('goalGame.starSearch')} />
                <button onClick={() => setPicking(false)} data-testid="remote-star-close"
                        className="mt-4 w-full min-h-[44px] rounded-xl border border-white/25 text-sm font-semibold">
                    {t('goalGame.starSkip')}
                </button>
            </div>
        );
    }

    const points = (
        <div className={`${panel} mt-6`} data-testid="remote-points-card">
            <p className="text-sm font-bold" data-testid="remote-signed-in">
                {t('goalGame.remoteSignedIn', { name: me.screen_name || '' })}
            </p>
            <p className="text-2xl font-black tabular-nums" style={{ color: c.accent }}
               data-testid="remote-points">
                {t('goalGame.remotePoints', { points: Number(me.points || 0).toLocaleString() })}
            </p>
            {me.star_available && (
                <>
                    <StarBar star={me.pick ? { name: me.pick.player_name } : null}
                             label={t('goalGame.starBarLabel')}
                             emptyLabel={t('goalGame.starBarEmpty')}
                             onOpen={() => setPicking(true)} c={c} locked={false} />
                    {me.pick && !state.event?.status?.includes('open') && (
                        <button onClick={() => setPicking(true)} data-testid="remote-star-change"
                                className="mt-2 text-xs underline opacity-70">
                            {t('goalGame.remoteStarChange')}
                        </button>
                    )}
                    <button onClick={toggleKeep} data-testid="remote-keep-star"
                            className="mt-3 flex items-start gap-3 text-left min-h-[44px]">
                        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-white/40"
                              style={me.keep_star ? { backgroundColor: c.button, borderColor: c.button } : undefined}>
                            {me.keep_star && <Check size={14} />}
                        </span>
                        <span className="text-sm">{t('goalGame.remoteKeepStar')}</span>
                    </button>
                </>
            )}
            <MyPrizes prizes={state.prizes} c={c} t={t} lang={lang} />
        </div>
    );

    // ── the scratch just finished, or this player already played this goal ──
    if (outcome && !outcome.already_played) {
        return shell(
            <>
                <div className="rounded-2xl p-8 text-center border"
                     style={won
                         ? { backgroundColor: 'rgba(255,255,255,0.09)', borderColor: c.button,
                             boxShadow: `0 0 48px ${c.button}55` }
                         : { backgroundColor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.12)' }}
                     data-testid={won ? 'remote-result-win' : 'remote-result-lose'}>
                    <p className="font-black mb-3" style={won ? { ...ty.headline, color: c.button } : ty.headline}>
                        {won ? t('goalGame.youWon') : t('goalGame.loseTitle')}
                    </p>
                    {won ? (
                        <>
                            <p className="text-2xl font-extrabold" style={{ color: c.accent }}
                               data-testid="remote-prize-title">{prizeTitle(outcome.prize, lang)}</p>
                            {outcome.voucher?.code && (
                                <div className="mt-4 rounded-2xl border border-white/20 bg-black/30 px-4 py-3"
                                     data-testid="remote-voucher">
                                    <p className="text-[10px] font-semibold uppercase tracking-wider opacity-60">
                                        {t('goalGame.voucherLabel')}
                                    </p>
                                    <p className="text-xl font-black tracking-[0.18em] font-mono"
                                       style={{ color: c.accent }} data-testid="remote-voucher-code">
                                        {outcome.voucher.code}
                                    </p>
                                    <p className="text-[11px] opacity-70">{t('goalGame.voucherHint')}</p>
                                </div>
                            )}
                            {outcome.prize_email?.sent && (
                                <p className="mt-3 text-xs opacity-60" data-testid="remote-prize-emailed">
                                    {t('goalGame.remoteEmailSent')}
                                </p>
                            )}
                        </>
                    ) : <p className="text-sm opacity-60">{t('goalGame.loseBody')}</p>}
                </div>
                {points}
            </>
        );
    }

    if (open && !state.played) {
        return shell(
            <>
                {state.event.goal_light && <GoalBeacon />}
                <div className="text-center mb-5">
                    <p className="text-4xl font-black uppercase"
                       style={{ ...ty.headline, color: c.button, textShadow: `0 0 24px ${c.button}66` }}
                       data-testid="remote-banner">{t('goalGame.goalBanner')}</p>
                    <p className="text-sm opacity-60 mt-1">{t('goalGame.scratchNow')}</p>
                    <p className="text-5xl font-black tabular-nums mt-3" data-testid="remote-countdown">{left}</p>
                    <p className="text-xs uppercase tracking-widest opacity-40">{t('goalGame.secondsLeft')}</p>
                </div>
                <div className="relative h-44 rounded-2xl overflow-hidden bg-slate-800 border border-white/10"
                     data-testid="remote-scratch-area">
                    <div className="absolute inset-0 flex items-center justify-center px-6 text-center">
                        <p className="text-sm opacity-70">{t('goalGame.scratchHidden')}</p>
                    </div>
                    <GoalScratch onComplete={onScratched} label={t('goalGame.scratchHint')} />
                </div>
            </>
        );
    }

    if (state.played) {
        return shell(
            <>
                <div className={`${panel} text-center`} data-testid="remote-already-played">
                    <p className="text-lg font-bold mb-2" style={ty.headline}>
                        {t('goalGame.remotePlayedTitle')}
                    </p>
                    <p className="text-sm opacity-60">{t('goalGame.remotePlayedBody')}</p>
                    {state.result === 'win' && state.voucher?.code && (
                        <p className="mt-4 font-semibold" style={{ color: c.accent }}>
                            {t('goalGame.youWon')}: {prizeTitle(state.prize, lang)}
                            <span className="block font-mono tracking-[0.18em]"
                                  data-testid="remote-played-voucher">{state.voucher.code}</span>
                        </p>
                    )}
                </div>
                {points}
            </>
        );
    }

    return shell(
        <>
            <div className={`${panel} text-center`} data-testid="remote-no-round">
                <Star size={18} className="mx-auto mb-2" style={{ color: c.accent }} />
                <p className="text-base font-semibold mb-1" style={ty.headline}>
                    {t('goalGame.remoteNoRoundTitle')}
                </p>
                <p className="text-sm opacity-60">
                    {t('goalGame.remoteNoRoundBody', {
                        team: state.venue,
                        seconds: state.event?.window_seconds || 300,
                    })}
                </p>
                {state.scratches_used >= state.max_scratches && (
                    <p className="mt-3 text-xs opacity-70" data-testid="remote-limit">
                        {t('goalGame.remoteLimitReached')}
                    </p>
                )}
            </div>
            {points}
        </>
    );
}
